import type { FC } from 'react';

import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { LoadingButton } from '@mui/lab';
import type { DialogProps } from '@mui/material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

export interface ConfirmationDialogProps extends DialogProps {
  onConfirm?: () => void;

  loading?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;

  withoutDialogActions?: boolean;
  withoutCancelButton?: boolean;

  disabled?: boolean;
  error?: string | null;

  allowBackdropClose?: boolean;
}

export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  onConfirm,
  onClose,
  loading = false,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  withoutDialogActions = false,
  withoutCancelButton = false,
  disabled = false,
  error,
  id,
  title,
  open,
  sx,
  children,
  allowBackdropClose = false,
  ...props
}) => {
  const prefix = 'confirmation-dialog';

  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (loading) return;
    if (!allowBackdropClose && reason === 'backdropClick') return;
    onClose?.(event, reason);
  };

  return (
    <Dialog
      id={id || prefix}
      data-testid={id || prefix}
      scroll='paper'
      aria-labelledby={`${id || prefix}-title`}
      aria-describedby={`${id || prefix}-description`}
      onClose={handleClose}
      sx={sx}
      open={open}
      fullWidth
      {...props}
    >
      <DialogTitle id={`${id || prefix}-title`} variant='h6'>
        {title}
      </DialogTitle>

      <DialogContent id={`${id || prefix}-content`}>{children}</DialogContent>

      {!withoutDialogActions && (
        <DialogActions id={`${id || prefix}-actions`}>
          {error && (
            <Stack flex='1 0 auto' display='flex' direction='row' alignItems='center' spacing={1}>
              <InfoOutlined fontSize='small' color='error' />
              <Typography variant='body2' color='error'>
                {error}
              </Typography>
            </Stack>
          )}

          <Stack direction='row' spacing={1}>
            {!withoutCancelButton && (
              <Button
                variant='outlined'
                size='medium'
                disabled={loading}
                onClick={(event) => {
                  if (loading) return;
                  onClose?.(event, 'backdropClick');
                }}
                data-testid={`${id || prefix}-cancel-button`}
              >
                {cancelButtonText}
              </Button>
            )}

            <LoadingButton
              color='primary'
              variant='contained'
              onClick={onConfirm}
              disabled={disabled}
              loading={loading}
              data-testid={`${id || prefix}-confirm-button`}
            >
              {confirmButtonText}
            </LoadingButton>
          </Stack>
        </DialogActions>
      )}
    </Dialog>
  );
};
